// ============================================
// ActivateAccount — /activar-cuenta?token=…
// Clientas importadas de WordPress: crean su contraseña, completan su perfil
// de piel y reciben un cupón único (backend: /store/account-activation).
// Si ya tenían cuenta en el sitio nuevo, solo completan el perfil.
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MEDUSA_API_URL, MEDUSA_API_KEY } from '../services/medusa/config';
import { useCustomer } from '../context/CustomerContext';
import { SKIN_ATTRS, GENDER_OPTIONS } from '../data/skinProfile';
import SEO from '../components/SEO';
import './ForgotPassword.css';
import './ActivateAccount.css';

const MIN_LEN = 8;
const headers = (extra = {}) => ({
  'Content-Type': 'application/json',
  'x-publishable-api-key': MEDUSA_API_KEY,
  ...extra,
});

// nombre del campo en el formulario → clave en el backend
const PROFILE_KEYS = { sebum: 'sebum', ageRange: 'age_range', sensitive: 'sensitive_skin', skinType: 'skin_type' };

export default function ActivateAccount() {
  const location = useLocation();
  const { setSession } = useCustomer();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);

  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({ password: '', confirm: '', gender: '', sebum: '', ageRange: '', sensitive: '', skinType: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) { setLoadError('Falta el enlace de activación.'); return; }
    fetch(`${MEDUSA_API_URL}/store/account-activation?token=${encodeURIComponent(token)}`, { headers: headers() })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || 'El enlace no es válido o ya venció.');
        setInfo(data);
        setForm((f) => ({
          ...f,
          gender: data.profile?.gender && data.profile.gender !== 'x' ? data.profile.gender : '',
          sebum: data.profile?.sebum || '',
          ageRange: data.profile?.age_range || '',
          sensitive: data.profile?.sensitive_skin || '',
          skinType: data.profile?.skin_type || '',
        }));
      })
      .catch((e) => setLoadError(e.message));
  }, [token]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const needsPassword = !info?.has_password;
    if (needsPassword) {
      if (form.password.length < MIN_LEN) return setError(`La contraseña debe tener al menos ${MIN_LEN} caracteres.`);
      if (form.password !== form.confirm) return setError('Las contraseñas no coinciden.');
    }
    const profile = { gender: form.gender || 'x' };
    Object.entries(PROFILE_KEYS).forEach(([k, key]) => { if (form[k]) profile[key] = form[k]; });

    setLoading(true);
    try {
      const r = await fetch(`${MEDUSA_API_URL}/store/account-activation`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ token, password: needsPassword ? form.password : undefined, profile }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || 'No pudimos activar tu cuenta.');

      // Inicia sesión con la contraseña recién creada.
      if (needsPassword) {
        try {
          const auth = await fetch(`${MEDUSA_API_URL}/auth/customer/emailpass`, {
            method: 'POST',
            credentials: 'include',
            headers: headers(),
            body: JSON.stringify({ email: data.email, password: form.password }),
          }).then((x) => x.json());
          if (auth?.token) {
            const me = await fetch(`${MEDUSA_API_URL}/store/customers/me`, {
              credentials: 'include',
              headers: headers({ Authorization: `Bearer ${auth.token}` }),
            }).then((x) => x.json());
            if (me?.customer) setSession(auth.token, me.customer);
          }
        } catch { /* si falla el login automático, la cuenta igual quedó activa */ }
      }
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const discount = info?.discount || result?.discount || '15%';

  return (
    <div className="forgot-page" data-header-theme="light">
      <SEO title="Activa tu cuenta" description="Activa tu cuenta Physiology y recibe tu regalo." noindex />
      <div className="forgot-card activate-card">
        {result ? (
          <div className="forgot-success">
            <p className="forgot-eyebrow">Cuenta activa</p>
            <h1 className="forgot-success-headline">
              {form.gender === 'f' ? 'Bienvenida de vuelta' : form.gender === 'm' ? 'Bienvenido de vuelta' : 'Qué gusto tenerte de vuelta'}
              {info?.first_name ? `, ${info.first_name}` : ''}.
            </h1>
            <p className="forgot-success-body">
              Tu perfil de piel quedó guardado. Este es tu código de <strong>{result.discount}</strong> para
              tu próxima compra (un solo uso). También te lo enviamos por correo.
            </p>
            <div className="activate-code">{result.code}</div>
            {result.already_had_account && (
              <p className="forgot-success-body">Ya tenías cuenta en el sitio nuevo: inicia sesión con tu contraseña de siempre.</p>
            )}
            <Link to="/shop" className="forgot-submit activate-cta">Ir a la tienda</Link>
          </div>
        ) : loadError ? (
          <div className="forgot-success">
            <p className="forgot-eyebrow">Activa tu cuenta</p>
            <h1 className="forgot-success-headline">Este enlace no funciona.</h1>
            <p className="forgot-success-body">{loadError} Escríbenos a hello@physiologyskincare.com y te ayudamos.</p>
            <Link to="/" className="forgot-back">Volver al inicio</Link>
          </div>
        ) : !info ? (
          <p className="forgot-lead">Cargando…</p>
        ) : (
          <>
            <div className="forgot-header">
              <p className="forgot-eyebrow">Estrenamos sitio</p>
              <h1 className="forgot-title">Activa tu cuenta{info.first_name ? `, ${info.first_name}` : ''}</h1>
              <p className="forgot-lead">
                {info.has_password
                  ? 'Ya tienes contraseña en el sitio nuevo. Cuéntanos de tu piel y recibe tu regalo.'
                  : 'Crea tu contraseña y cuéntanos de tu piel.'}{' '}
                Te regalamos <strong>{discount}</strong> en tu próxima compra.
              </p>
            </div>

            <form className="forgot-form" onSubmit={handleSubmit} noValidate>
              <label className="forgot-label">Correo</label>
              <input className="forgot-input" value={info.email} disabled readOnly />

              {!info.has_password && (
                <>
                  <label className="forgot-label" htmlFor="password">Nueva contraseña</label>
                  <input id="password" name="password" type="password" className="forgot-input" autoComplete="new-password"
                    value={form.password} onChange={onChange} disabled={loading} required />
                  <label className="forgot-label" htmlFor="confirm">Confirma tu contraseña</label>
                  <input id="confirm" name="confirm" type="password" className="forgot-input" autoComplete="new-password"
                    value={form.confirm} onChange={onChange} disabled={loading} required />
                </>
              )}

              <label className="forgot-label" htmlFor="gender">Género <span className="activate-hint">(para dirigirnos a ti correctamente)</span></label>
              <select id="gender" name="gender" className="forgot-input activate-select" value={form.gender} onChange={onChange} disabled={loading}>
                {GENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>

              <p className="forgot-label">Sobre tu piel <span className="activate-hint">(para recomendaciones a tu medida)</span></p>
              <div className="activate-grid">
                {SKIN_ATTRS.map((a) => (
                  <select key={a.name} name={a.name} aria-label={a.label} className="activate-attr" value={form[a.name]} onChange={onChange} disabled={loading}>
                    <option value="">{a.label}</option>
                    {a.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ))}
              </div>

              {error && <p className="forgot-error" role="alert">{error}</p>}

              <button type="submit" className="forgot-submit" disabled={loading}>
                {loading ? 'Activando…' : `Activar y recibir mi ${discount}`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
